<template>
  <div ref="chart" class="bp-trend-chart"></div>
</template>

<script>
// echarts 为 CDN 定制压缩版（vue.config externals），通过 window.echarts 懒取，
// 避免静态 import 在 CDN 脚本就绪前触发 "echarts is not defined"。
import { fmtNum } from '../format'

// 三系列配置（顺序：曝光 / 浏览 / 互动）
// legend=图例圆点（饱和色）；barGrad=柱状渐变（上→下）；lineGrad=折线渐变（左→右）；dot=折线数据点；label=数值气泡文字/边框
const SERIES_META = [
  {
    key: 'exposure', name: '曝光量',
    legend: '#25F2EC',
    barGrad: ['#6EF2EE', '#15DCD8'],
    lineGrad: ['#77FFFB', '#00E8E1'],
    dot: '#77FFFB',
    label: '#00CFC9'
  },
  {
    key: 'views', name: '浏览量',
    legend: '#2561FF',
    barGrad: ['#3A89FF', '#2863FF'],
    lineGrad: ['#78B1FF', '#378CFE'],
    dot: '#78B1FF',
    label: '#3A89FF'
  },
  {
    key: 'interaction', name: '互动量',
    legend: '#FF213B',
    barGrad: ['#FF7E81', '#FF455A'],
    lineGrad: ['#FFA8AA', '#FF455A'],
    dot: '#FFA8AA',
    label: '#FF5160'
  }
]

export default {
  name: 'BpTrendChart',
  props: {
    // 'line'（小红书）| 'bar'（抖音）
    chartType: { type: String, default: 'line' },
    // 月份横坐标，按数据展示，如 ['5月','6月','7月','8月']
    months: { type: Array, default: () => [] },
    // { exposure:[], views:[], interaction:[] } 各项与 months 等长
    series: { type: Object, default: () => ({}) }
  },
  data () {
    return { chart: null, retryTimer: null }
  },
  watch: {
    chartType () { this.render() },
    months () { this.render() },
    series: { handler () { this.render() }, deep: true }
  },
  mounted () {
    window.addEventListener('resize', this.resize)
    this.tryInit()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resize)
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    // CDN 定制版 echarts 刷新时可能尚未挂到 window，未就绪则短暂重试，就绪后再初始化
    tryInit () {
      const ec = window.echarts
      if (!ec) {
        this.retryTimer = setTimeout(this.tryInit, 60)
        return
      }
      this.retryTimer = null
      this.chart = ec.init(this.$refs.chart)
      this.render()
    },
    resize () {
      if (this.chart) this.chart.resize()
    },
    // 数值气泡样式（show 由各数据项控制，仅首尾点显示）
    labelStyle (color) {
      return {
        show: true,
        color,
        backgroundColor: 'rgba(255,255,255,0.9)',
        borderColor: color,
        borderWidth: 0.5,
        borderRadius: 4,
        padding: [2, 4],
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'D-DIN-PRO, Arial, sans-serif'
      }
    },
    // 将数值数组转为数据项：仅首尾点显示 label，中间点 show:false 彻底不渲染（边框也无）
    edgeData (arr) {
      const last = arr.length - 1
      return arr.map((v, i) => ({
        value: v,
        label: { show: i === 0 || i === last }
      }))
    },
    buildLineSeries () {
      return SERIES_META.map(m => ({
        name: m.name,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 10,
        itemStyle: { color: m.dot, borderColor: '#fff', borderWidth: 2.5 },
        lineStyle: {
          width: 2,
          color: {
            type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
            colorStops: [
              { offset: 0, color: m.lineGrad[0] },
              { offset: 1, color: m.lineGrad[1] }
            ]
          }
        },
        label: { ...this.labelStyle(m.label), position: 'top', formatter: (p) => fmtNum(Array.isArray(p.value) ? p.value[1] : p.value) },
        // value 轴用 [index, value] 坐标对；仅首尾点显示数值气泡
        data: (this.series[m.key] || []).map((v, i, arr) => ({
          value: [i, v],
          label: { show: i === 0 || i === arr.length - 1 }
        }))
      }))
    },
    buildBarSeries () {
      return SERIES_META.map(m => ({
        name: m.name,
        type: 'bar',
        barWidth: 10,
        barGap: '80%',
        itemStyle: {
          barBorderRadius: [5, 5, 0, 0],
          borderRadius: [5, 5, 0, 0],
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: m.barGrad[0] },
              { offset: 1, color: m.barGrad[1] }
            ]
          }
        },
        label: { ...this.labelStyle(m.label), position: 'top', formatter: (p) => fmtNum(Array.isArray(p.value) ? p.value[1] : p.value) },
        data: this.edgeData(this.series[m.key] || [])
      }))
    },
    render () {
      if (!this.chart) return
      const isBar = this.chartType === 'bar'
      const series = isBar ? this.buildBarSeries() : this.buildLineSeries()
      const axisLineStyle = { show: true, lineStyle: { color: 'rgba(158,179,232,0.5)' } }
      const n = this.months.length
      // 柱状：类目轴 boundaryGap:true（柱子居中），interval:0 每月都标。
      // 折线：value 轴，min/max 各留 0.4 单位空隙（首尾点不贴轴/边缘）；minInterval:1 使刻度落在整数索引，
      //       formatter 按索引取月份；边界 -0.4 / n-1+0.4 处取不到月份返回 ''（不显示文字）。
      const xAxis = isBar
        ? {
          type: 'category',
          boundaryGap: true,
          data: this.months,
          axisLine: axisLineStyle,
          axisTick: { show: false },
          axisLabel: { color: '#8D929A', fontSize: 14, interval: 0 },
          splitLine: { show: false }
        }
        : {
          type: 'value',
          min: -0.2,
          // 多月：首尾各留 0.2 空隙；单月：范围对称(-0.2~0.2)使唯一点居中
          max: n > 1 ? (n - 1) + 0.2 : 0.2,
          minInterval: 1,
          axisLine: axisLineStyle,
          axisTick: { show: false },
          axisLabel: { color: '#8D929A', fontSize: 14, formatter: (v) => this.months[v] || '' },
          splitLine: { show: false }
        }
      this.chart.setOption({
        grid: { left: 8, right: 16, top: 56, bottom: 8, containLabel: true },
        legend: {
          show: true,
          top: 0,
          left: 'center',
          icon: 'circle',
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 84,
          textStyle: { color: '#8D929A', fontSize: 16 },
          data: SERIES_META.map(m => ({ name: m.name, itemStyle: { color: m.legend } }))
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const list = Array.isArray(params) ? params : [params]
            const first = list[0] || {}
            // 折线用 value 轴（x 为索引），标题取对应月份；柱状类目轴直接用 axisValueLabel
            const title = this.months[first.dataIndex] || first.axisValueLabel || ''
            const rows = list.map(p => {
              const val = Array.isArray(p.value) ? p.value[1] : p.value
              return `${p.marker}${p.seriesName}：${fmtNum(val)}`
            }).join('<br/>')
            return title ? `${title}<br/>${rows}` : rows
          }
        },
        xAxis,
        yAxis: {
          type: 'value',
          axisLine: { show: true, onZero: false, lineStyle: { color: 'rgba(158,179,232,0.5)' } },
          axisTick: { show: false },
          axisLabel: { color: '#8D929A', fontSize: 13, formatter: (v) => fmtNum(v) },
          splitLine: { lineStyle: { type: 'dashed', color: '#D6DDFF' } }
        },
        series
      }, true)
    }
  }
}
</script>

<style lang="less" scoped>
.bp-trend-chart {
  width: 100%;
  height: 260px;
}
</style>
